'use client'

import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { LocationMapHeroRouteDirection } from '@/components/Location/LocationMapHeroMap'
import { applyUniversityFinderSelection } from '@/lib/locations/berlinUniversityFinder'
import {
  getUniversityFinderTravelStats,
  hasUniversityCoordinates,
} from '@/lib/locations/universityDataset'
import { getCampusZoomBetweenPoints } from '@/lib/geo'
import { resolveUniversityTravelStats } from '@/lib/travelStats'
import type { City, CityAreaMapCameraPadding } from '@/lib/mock/cities'
import type { CityId, LngLat } from '@/lib/mock/mapLocations'
import {
  getLocationCampusBySlug,
  getLocationCampusesForCity,
  getLocationHeroMapViewport,
  getLocationMapHeroCityViewport,
  getLocationPageBySlug,
  getUniversitiesForCity,
  type LocationCampusOption,
  type LocationPage,
  type LocationUniversityOption,
} from '@/lib/mock/locationPages'

const MOBILE_MAP_MEDIA_QUERY = '(max-width: 767px)'
const MOBILE_STACKED_MAP_HEIGHT_PX = 320
const MOBILE_ZOOM_MAX_WIDTH_PX = 767
const MOBILE_ZOOM_MIN_WIDTH_PX = 320
const MOBILE_ZOOM_OFFSET_AT_MAX_WIDTH = -0.25
const MOBILE_ZOOM_OFFSET_AT_MIN_WIDTH = -1
/** Pull mobile map framing slightly closer than the responsive zoom-out curve. */
const MOBILE_MAP_ZOOM_BOOST = 2
/** Extra zoom-out on city pages so the taller map shows more of the city. */
const CITY_ZOOM_EXTRA_OFFSET = -0.85

export const UNIVERSITY_FINDER_CITY_IDS: CityId[] = [
  'berlin',
  'cologne',
  'frankfurt',
  'tuebingen',
]

export type CityMapUniversityFinderCopy = {
  approximateDistanceLabel: string
  distanceFromToLabel: string
  distanceToLabel: string
  proximityLabel: string
  routeUnavailable: string
}

export type CityMapUniversityFinderHeroViewport = {
  center: LngLat
  markers: Array<{
    latitude: number
    longitude: number
    name: string
    slug: string
  }>
  zoom: number
}

export type UseCityMapUniversityFinderParams = {
  /** When false, picking a university never overwrites or fills the location dropdown. */
  autoSelectNearestCampus?: boolean
  city?: City
  citySlug?: CityId
  fixedLocation?: LocationPage
  isCityMode: boolean
  mapContainerRef?: RefObject<HTMLElement | null>
  mapHeroCopy: CityMapUniversityFinderCopy
}

function getMobileMapZoomOffset(viewportWidth: number): number {
  const clampedWidth = Math.min(
    MOBILE_ZOOM_MAX_WIDTH_PX,
    Math.max(MOBILE_ZOOM_MIN_WIDTH_PX, viewportWidth),
  )
  const ratio =
    (MOBILE_ZOOM_MAX_WIDTH_PX - clampedWidth) /
    (MOBILE_ZOOM_MAX_WIDTH_PX - MOBILE_ZOOM_MIN_WIDTH_PX)

  return (
    MOBILE_ZOOM_OFFSET_AT_MAX_WIDTH +
    ratio * (MOBILE_ZOOM_OFFSET_AT_MIN_WIDTH - MOBILE_ZOOM_OFFSET_AT_MAX_WIDTH) +
    MOBILE_MAP_ZOOM_BOOST
  )
}

function getMobileStackedMapCameraPadding(mapHeight: number): CityAreaMapCameraPadding {
  const edgeInset = Math.max(16, Math.round(mapHeight * 0.1))

  return {
    bottom: edgeInset,
    disableContentInset: true,
    minLeft: edgeInset,
    right: edgeInset,
    top: edgeInset,
  }
}

function toUniversityCoordinate(university: LocationUniversityOption): LngLat | undefined {
  if (university.latitude == null || university.longitude == null) {
    return undefined
  }

  return [university.longitude, university.latitude]
}

/** Short pin label for a single selected campus (e.g. "Berlin Mitte-Wedding" → "Mitte-Wedding"). */
export function toShortCampusMapLabel(name: string): string {
  return name
    .replace(/^Tannhaus Berlin\s+/i, '')
    .replace(/^(Berlin|Cologne|Frankfurt)\s*,?\s+/i, '')
    .trim()
}

export function formatDistanceToLabel(template: string, name: string): string {
  return template.replace('{name}', name)
}

export function formatDistanceFromToLabel(template: string, from: string, to: string): string {
  return template.replace('{from}', from).replace('{to}', to)
}

export function isUniversityFinderCity(citySlug: CityId | undefined, isCityMode: boolean): boolean {
  return Boolean(isCityMode && citySlug && UNIVERSITY_FINDER_CITY_IDS.includes(citySlug))
}

/** Map pin selection for the landing city overview map — campus slug when a city is active. */
export function getLandingMapSelectedLocationId(
  citySlug: CityId | undefined,
  selectedCampusSlug: string,
): string {
  return citySlug ? selectedCampusSlug : ''
}

export function useCityMapUniversityFinder({
  autoSelectNearestCampus = true,
  city,
  citySlug,
  fixedLocation,
  isCityMode,
  mapContainerRef,
  mapHeroCopy,
}: UseCityMapUniversityFinderParams) {
  const isUniversityFinder = isUniversityFinderCity(citySlug, isCityMode)

  const campuses = useMemo(
    () => (citySlug ? getLocationCampusesForCity(citySlug) : []),
    [citySlug],
  )
  const universities = useMemo(
    () => (citySlug ? getUniversitiesForCity(citySlug) : []),
    [citySlug],
  )
  const universityCampusOptions = useMemo(
    () =>
      universities.filter(
        (university): university is LocationUniversityOption & LocationCampusOption =>
          university.latitude != null && university.longitude != null,
      ),
    [universities],
  )

  const soleCampusSlug = isCityMode && campuses.length === 1 ? (campuses[0]?.slug ?? '') : ''
  const initialCampusSlug = fixedLocation?.slug ?? soleCampusSlug

  const [selectedCampusSlug, setSelectedCampusSlug] = useState(initialCampusSlug)
  const [selectedUniversity, setSelectedUniversity] = useState<LocationUniversityOption | null>(
    null,
  )
  const [routeAnimationId, setRouteAnimationId] = useState(0)
  const [routeDirection, setRouteDirection] = useState<LocationMapHeroRouteDirection | undefined>()
  const [mobileMapCameraPadding, setMobileMapCameraPadding] = useState<
    CityAreaMapCameraPadding | undefined
  >()
  const [mobileMapZoomOffset, setMobileMapZoomOffset] = useState(0)
  const [isMobileMap, setIsMobileMap] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MAP_MEDIA_QUERY).matches,
  )
  const [mapCitySlug, setMapCitySlug] = useState(citySlug)

  if (citySlug !== mapCitySlug) {
    setMapCitySlug(citySlug)
    setSelectedCampusSlug(initialCampusSlug)
    setSelectedUniversity(null)
    setRouteDirection(undefined)
  } else if (soleCampusSlug && selectedCampusSlug !== soleCampusSlug) {
    setSelectedCampusSlug(soleCampusSlug)
  }

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MAP_MEDIA_QUERY)
    const mapContainer = mapContainerRef?.current

    const updatePadding = () => {
      const mobile = media.matches
      setIsMobileMap(mobile)

      if (!mobile) {
        setMobileMapCameraPadding(undefined)
        setMobileMapZoomOffset(0)
        return
      }

      const mapHeight = mapContainer?.clientHeight ?? MOBILE_STACKED_MAP_HEIGHT_PX
      setMobileMapCameraPadding(getMobileStackedMapCameraPadding(mapHeight))
      setMobileMapZoomOffset(getMobileMapZoomOffset(window.innerWidth))
    }

    updatePadding()
    media.addEventListener('change', updatePadding)
    window.addEventListener('resize', updatePadding)

    const resizeObserver =
      mapContainer && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updatePadding)
        : undefined

    if (mapContainer && resizeObserver) {
      resizeObserver.observe(mapContainer)
    }

    return () => {
      media.removeEventListener('change', updatePadding)
      window.removeEventListener('resize', updatePadding)
      resizeObserver?.disconnect()
    }
  }, [mapContainerRef])

  const activeLocation = useMemo(() => {
    if (!citySlug) {
      return undefined
    }

    if (fixedLocation && !isCityMode) {
      return fixedLocation
    }

    if (!selectedCampusSlug) {
      return undefined
    }

    return getLocationPageBySlug(citySlug, selectedCampusSlug)
  }, [citySlug, fixedLocation, isCityMode, selectedCampusSlug])

  const currentCampus = useMemo(() => {
    if (!citySlug || !selectedCampusSlug) {
      return undefined
    }

    return getLocationCampusBySlug(citySlug, selectedCampusSlug)
  }, [citySlug, selectedCampusSlug])

  const heroMapViewport = useMemo((): CityMapUniversityFinderHeroViewport | undefined => {
    if (!citySlug) {
      return undefined
    }

    if (isCityMode && city) {
      const cityViewport = getLocationMapHeroCityViewport(citySlug, city.areaMap.zoom)
      const cityZoomOffset =
        (isMobileMap ? mobileMapZoomOffset : 0) + CITY_ZOOM_EXTRA_OFFSET

      if (!selectedCampusSlug) {
        return {
          ...cityViewport,
          zoom: cityViewport.zoom + cityZoomOffset,
        }
      }

      const selectedCampus = getLocationCampusBySlug(citySlug, selectedCampusSlug)

      if (!selectedCampus) {
        return {
          ...cityViewport,
          zoom: cityViewport.zoom + cityZoomOffset,
        }
      }

      const campusCoordinate: LngLat = [selectedCampus.longitude, selectedCampus.latitude]
      const campusZoom = getCampusZoomBetweenPoints(
        cityViewport.center,
        campusCoordinate,
        city.areaMap.zoom,
      )

      return {
        center: cityViewport.center,
        zoom: campusZoom + cityZoomOffset,
        markers: [
          {
            slug: selectedCampusSlug,
            name: selectedCampus.name,
            latitude: selectedCampus.latitude,
            longitude: selectedCampus.longitude,
          },
        ],
      }
    }

    if (activeLocation) {
      const viewport = getLocationHeroMapViewport(activeLocation)
      const markerName = viewport.marker?.name ?? activeLocation.name

      return {
        center: viewport.center,
        zoom: viewport.zoom + (isMobileMap ? mobileMapZoomOffset : 0),
        markers: viewport.marker
          ? [
              {
                slug: activeLocation.slug,
                name: toShortCampusMapLabel(markerName),
                latitude: viewport.marker.latitude,
                longitude: viewport.marker.longitude,
              },
            ]
          : [],
      }
    }

    return undefined
  }, [
    activeLocation,
    city,
    citySlug,
    isCityMode,
    isMobileMap,
    mobileMapZoomOffset,
    selectedCampusSlug,
  ])

  const showUniversityRoute = Boolean(
    selectedUniversity && currentCampus && hasUniversityCoordinates(selectedUniversity),
  )

  const routeMarker = useMemo(() => {
    if (!showUniversityRoute || !currentCampus) {
      return undefined
    }

    return {
      latitude: currentCampus.latitude,
      longitude: currentCampus.longitude,
    }
  }, [currentCampus, showUniversityRoute])

  const universityCoordinate = useMemo(() => {
    if (!showUniversityRoute || !selectedUniversity) {
      return undefined
    }

    return toUniversityCoordinate(selectedUniversity)
  }, [selectedUniversity, showUniversityRoute])

  const universityTravelStats = useMemo(() => {
    if (!selectedUniversity) {
      return undefined
    }

    const resolved = currentCampus
      ? resolveUniversityTravelStats({
          origin: currentCampus,
          destination: selectedUniversity,
        })
      : undefined

    if (resolved?.length) {
      return resolved
    }

    if (isUniversityFinder && currentCampus) {
      return getUniversityFinderTravelStats(selectedUniversity, currentCampus, {
        approximateDistanceLabel: mapHeroCopy.approximateDistanceLabel,
        proximityLabel: mapHeroCopy.proximityLabel,
      })
    }

    return undefined
  }, [
    currentCampus,
    isUniversityFinder,
    mapHeroCopy.approximateDistanceLabel,
    mapHeroCopy.proximityLabel,
    selectedUniversity,
  ])

  const desktopMapCameraPadding =
    city?.areaMap.mapCameraPadding ?? activeLocation?.areaMap.mapCameraPadding
  const mapCameraPadding = isMobileMap ? mobileMapCameraPadding : desktopMapCameraPadding

  const handleUniversitySelect = useCallback(
    (university: LocationUniversityOption) => {
      if (isUniversityFinder) {
        const nextSelection = applyUniversityFinderSelection({
          autoSelectNearestCampus,
          campuses,
          university,
        })

        setSelectedUniversity(nextSelection.selectedUniversity)
        setSelectedCampusSlug(nextSelection.selectedCampusSlug)

        if (nextSelection.selectedCampusSlug) {
          setRouteDirection('university-to-campus')
          setRouteAnimationId((current) => current + 1)
        }

        return
      }

      if (selectedCampusSlug) {
        setRouteDirection('campus-to-university')
        setRouteAnimationId((current) => current + 1)
      }

      setSelectedUniversity(university)
    },
    [autoSelectNearestCampus, campuses, isUniversityFinder, selectedCampusSlug],
  )

  const handleUniversityClear = useCallback(() => {
    setSelectedUniversity(null)
    setRouteDirection(undefined)

    if (isUniversityFinder) {
      setSelectedCampusSlug(soleCampusSlug)
    }
  }, [isUniversityFinder, soleCampusSlug])

  const handleCampusSelect = useCallback(
    (campus: LocationCampusOption) => {
      if (selectedUniversity) {
        setRouteDirection('university-to-campus')
        setRouteAnimationId((current) => current + 1)
      }

      setSelectedCampusSlug(campus.slug)
    },
    [selectedUniversity],
  )

  const handleCampusClear = useCallback(() => {
    setSelectedCampusSlug('')
    setRouteDirection(undefined)
  }, [])

  const routeUnavailableMessage =
    isUniversityFinder &&
    selectedUniversity &&
    currentCampus &&
    !hasUniversityCoordinates(selectedUniversity)
      ? mapHeroCopy.routeUnavailable
      : undefined

  const travelStatsDestinationLabel =
    isUniversityFinder && selectedUniversity && currentCampus
      ? formatDistanceFromToLabel(
          mapHeroCopy.distanceFromToLabel,
          selectedUniversity.name,
          currentCampus.name,
        )
      : !isCityMode && selectedUniversity
        ? formatDistanceToLabel(mapHeroCopy.distanceToLabel, selectedUniversity.name)
        : undefined

  return {
    activeLocation,
    campuses,
    currentCampus,
    fallbackImage: city?.heroImage ?? activeLocation?.heroImage ?? '',
    fallbackImageAlt: city?.heroImageAlt ?? activeLocation?.heroImageAlt ?? '',
    handleCampusClear,
    handleCampusSelect,
    handleUniversityClear,
    handleUniversitySelect,
    heroMapViewport,
    isMobileMap,
    isUniversityFinder,
    mapCameraPadding,
    routeAnimationId,
    routeDirection,
    routeMarker,
    routeUnavailableMessage,
    selectedCampusSlug,
    selectedUniversity,
    showUniversityRoute,
    travelStatsDestinationLabel,
    universities,
    universityCampusOptions,
    universityCoordinate,
    universityTravelStats,
  }
}
